import React, { useState } from 'react';
import Lottie from 'react-lottie';

import { InstructionItem, User } from '../../../utils/models';
import { tutorialConstants } from '../../../utils/constants';
import { getTutorials, setActiveTutorial } from '../../../store/tutorials';
import { getCurrentUser } from '../../../services/authService';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import Conditional from '../../common/conditional';
import Instruction from './instruction';
import InstructionBox from '../instruction-box';
import Modal from '../modal';

import hi from '../../../assets/animations/hi.json';
import classNames from './style.module.scss';

interface WelcomeToExternalUserProps {
	instructionItems: InstructionItem[];
}

const WelcomeToExternalUser: React.FC<WelcomeToExternalUserProps> = ({
	instructionItems,
}) => {
	const [activeInstruction, setActiveInstruction] = useState(1);
	const [isWelcomeCompleted, setWelcomeCompleted] = useState(false);

	const dispatch = useAppDispatch();
	const tutorials = useAppSelector(getTutorials);
	const user = getCurrentUser() as User;

	const handleSetNextTutorial = (tutorialId: number | null) => {
		dispatch(setActiveTutorial(tutorialId));
	};

	const handleCloseModal = () => {
		dispatch(setActiveTutorial(tutorialConstants.MAYBE_LATER));
	};

	const handleNextStep = () => {
		const nextStep: number = activeInstruction + 1;
		if (nextStep <= instructionItems.length) setActiveInstruction(nextStep);
		else {
			handleSetNextTutorial(
				user.userType === 'external user'
					? tutorialConstants.EXTERNAL_DASHBOARD
					: tutorialConstants.INTERNAL_DASHBOARD
			);
		}
	};

	const initiateTutorial = () => {
		dispatch(setActiveTutorial(tutorialConstants.WELCOME));
	};

	return (
		<React.Fragment>
			<Instruction
				isVisible={tutorials.activeTutorial === tutorialConstants.INTRODUCTION}
				onProceed={initiateTutorial}
				title="Meet Velidon!"
				message="Hi, I'm Velidon! I'll be taking you through a quick tour of the application. Click the button below to get started or click the close button to continue later!"
			/>

			<Modal
				isVisible={tutorials.activeTutorial === tutorialConstants.WELCOME}
				onClose={handleCloseModal}
				style={{ height: 'auto' }}
			>
				<div className={classNames.instructionModalBody}>
					<Conditional isVisible={!isWelcomeCompleted}>
						<div className={classNames.instructionModalBodyWelcome}>
							<Lottie
								height={300}
								width={300}
								options={{
									loop: false,
									autoplay: true,
									animationData: hi,
									rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
								}}
								eventListeners={[
									{
										eventName: 'complete',
										callback: () => setWelcomeCompleted(true),
									},
								]}
							/>
						</div>
					</Conditional>

					<Conditional isVisible={isWelcomeCompleted}>
						<h3 className={classNames.instructionModalBodyHeader}>
							<i className="bi bi-signal"></i>
							Welcome to the IDMS!
						</h3>
						<p className={classNames.instructionModalBodySubHeader}>
							Here are a few things you can do on this app!
						</p>

						<div className="container-fluid">
							<div className="row">
								{instructionItems.map((item, index) => (
									<React.Fragment key={item.title}>
										<div className="col mt-4">
											<InstructionBox
												title={item.title}
												subtitle={item.message}
												isActive={activeInstruction === index + 1}
												animation={item.animation}
											/>
										</div>

										<Conditional isVisible={(index + 1) % 2 === 0}>
											<div className="w-100"></div>
										</Conditional>
									</React.Fragment>
								))}
							</div>
						</div>

						<div className={classNames.instructionModalBodyButtons}>
							<button
								type="button"
								className={classNames.instructionModalBodyButtonsItem}
								onClick={handleNextStep}
							>
								{activeInstruction === instructionItems.length
									? 'Get Started'
									: 'Next'}
							</button>
						</div>
					</Conditional>
				</div>
			</Modal>
		</React.Fragment>
	);
};

export default WelcomeToExternalUser;
